const ACCEPTED = new Set(['image/jpeg', 'image/png', 'image/webp']);
export const isAcceptedImage = (file: File): boolean => ACCEPTED.has(file.type);

/** Längste Seite des hochgeladenen Bildes (Pixel); der Server begrenzt genauso. */
export const MAX_SIDE = 1600;
/** Größere Dateien (z. B. unkomprimierte 8K-Aufnahmen) werden gar nicht erst geöffnet. */
export const MAX_FILE_BYTES = 40_000_000;
/** Mehr Pixel entpackt der Browser nicht sicher (ein 8K-Foto hat rund 33 Megapixel). */
export const MAX_PIXELS = 100_000_000;
/** Ziel für die Größe nach dem Verkleinern; das Limit des Servers liegt darüber. */
const TARGET_BYTES = 1_200_000;

/** Größe, in die ein Bild passt, ohne das Seitenverhältnis zu ändern; nie größer als das Original. */
export function fitWithin(width: number, height: number, max = MAX_SIDE): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export class ImageTooLargeError extends Error {}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Das Bild konnte nicht gelesen werden.'));
    r.readAsDataURL(file);
  });

const decode = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Das Bild konnte nicht gelesen werden.'));
    i.src = src;
  });

/**
 * Bereitet ein gewähltes Bild zum Hochladen vor: Große Fotos (z. B. 4K) werden im Browser auf höchstens `MAX_SIDE` Pixel an der
 * längsten Seite verkleinert (Seitenverhältnis bleibt, EXIF-Drehung wird angewendet) und als WebP kodiert. So wandern nur einige
 * Hundert KB statt mehrerer MB zum Server. Gelesen wird über eine Data-URL, denn die Content-Security-Policy erlaubt keine blob:-Bilder.
 */
export async function prepareImage(file: File, maxSide = MAX_SIDE): Promise<string> {
  if (file.size > MAX_FILE_BYTES) throw new ImageTooLargeError(`Die Datei ist zu groß (höchstens ${Math.round(MAX_FILE_BYTES / 1_000_000)} MB).`);
  const img = await decode(await readAsDataUrl(file));
  if (img.naturalWidth * img.naturalHeight > MAX_PIXELS) throw new ImageTooLargeError('Das Bild hat zu viele Pixel (höchstens 100 Megapixel).');

  let side = maxSide;
  let quality = 0.82;
  for (let attempt = 0; attempt < 4; attempt++) {
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, side);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Das Bild konnte nicht verarbeitet werden.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
    // WebP, wo der Browser es kann; sonst liefert der Browser PNG, dann lieber JPEG
    let url = canvas.toDataURL('image/webp', quality);
    if (!url.startsWith('data:image/webp')) url = canvas.toDataURL('image/jpeg', quality);
    if (url.length * 0.75 <= TARGET_BYTES || attempt === 3) return url;
    // sehr detailreiche Bilder: Qualität und Größe schrittweise senken
    quality = Math.max(0.6, quality - 0.1);
    side = Math.round(side * 0.8);
  }
  throw new Error('Das Bild konnte nicht verarbeitet werden.');
}
